import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { Box, Grid, InputLabel, TextField } from '@mui/material';
import { toNumber, toString, trim } from 'lodash';
import {
  type MouseEvent,
  type SyntheticEvent,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AssemblyLayout } from 'src/components/assemblyLayout';
import { AuthDataProvider } from 'src/components/authProvider';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { CustomerStateAlert } from 'src/components/customerStateAlert/CustomerStateAlert';
import { FullSizeAssemblyScreen } from 'src/components/fullSizeAssemblyScreen';
import { FullSizeAssemblySection } from 'src/components/fullSizeAssemblySection/FullSizeAssemblySection';
import { SimpleDialog } from 'src/components/modal';
import { Progress } from 'src/components/progress/Progress';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { Snackbar } from 'src/components/snackbar';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import {
  CardMediaContainer,
  ComponentMenuIcon,
} from 'src/features/components/detailComponentPage/DetailComponentPage.styled';
import {
  fetchAssemblyComponents,
  useClone,
  useDeleteVariation,
  useMenu,
  useModal,
  useUpdateAssembly,
  useUpdateCreateDeleteAssemblyComponents,
} from 'src/hooks';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { IAssembly, IAssemblyComponent, IStatus } from 'src/interfaces';
import {
  findMatchedGlobalComponents,
  generateAssemblyProductFilters,
  getExpiryStatusMessage,
  isDisallowedOutdoorSection,
  lang,
  useUserRole,
} from 'src/utils';
import { checkSectionTypes } from 'src/utils/section-types-helper';

import { AssemblyMenu } from '../listAssemblyPage/modules/assemblyMenu/AssemblyMenu';
import { getEarliestComponentExpiryDate } from '../listAssemblyPage/modules/assemblyTableRow/AssemblyTableRow.helper';
import { findMatchedAssemblyComponents } from './DetailAssemblyPage.helper';
import {
  ActionButtonsContainer,
  AntTab,
  AntTabs,
  AssemblyDetailContainer,
  AssemblyDetailContentContainer,
  AssemblyDetailSideMenuContainer,
  AssemblyNameText,
  HeaderContainer,
} from './DetailAssemblyPage.styled';
import { useDetailAssemblyPage } from './hooks/DetailAssemblyPage.hook';
import { AssemblyDetailPageSidebar } from './modules/AssemblyDetailPageSidebar';
import { DetailAssemblyComponentList } from './modules/DetailAssemblyComponentList';
import { DetailAssemblyTabPanel } from './modules/DetailAssemblyTabPanel/DetailAssemblyTabPanel';

export const DetailAssemblyPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    assemblyDetails,
    assemblyComponentsDetails,
    layouts,
    isAssemblyDetailsLoading,
    activeAssemblyJourney,
    setActiveAssemblyJourney,
    locationCode,
    isLoadingAssemblyOverrides,
    scheduleAssemblyId,
    customerState,
    setCustomerState,
    isDeleted,
    assemblyId,
    isSavedAssemblyIdMatched,
    isStageAssemblyIdMatched,
    globalComponentsData,
    onChangeDaypart,
    selectedDaypartId,
  } = useDetailAssemblyPage();

  const assemblyComponents = useMemo(
    () => [
      ...(assemblyComponentsDetails?.data || []),
      ...(assemblyComponentsDetails?.backupAssemblyComponents || []),
    ],
    [
      assemblyComponentsDetails?.data,
      assemblyComponentsDetails?.backupAssemblyComponents,
    ],
  );

  const isExpiringSoon = getExpiryStatusMessage(
    assemblyComponentsDetails &&
      getEarliestComponentExpiryDate(
        assemblyComponentsDetails.components || [],
      ),
  );

  const { userRole } = useContext(AuthDataProvider);
  const navigate = useNavigate();
  const location = useLocation();

  const sectionTypes = layouts[0]?.config?.sectionTypes;

  const [value, setValue] = useState(0);
  const [isCodConfirmationOpen, setIsCodConfirmationOpen] = useState<
    boolean | undefined
  >(undefined);

  const menu = useMenu();

  const deleteAssemblyModal = useModal();
  const duplicateAssemblyModal = useModal();
  const convertToDraftModal = useModal();

  const cloneResource = useClone();

  const [duplicatedAssemblyName, setDuplicatedAssemblyName] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [isDuplicated, setIsDuplicated] = useState(false);

  const removeVariation = useDeleteVariation();

  // Generate product filters based on assembly components
  const { productFilters, productVariationFilters } = useMemo(() => {
    return generateAssemblyProductFilters(
      assemblyComponentsDetails?.assemblyComponents ?? [],
      [
        ...(assemblyComponentsDetails?.components ?? []),
        ...(globalComponentsData ?? []),
      ],
    );
  }, [
    assemblyComponentsDetails?.components,
    assemblyComponentsDetails?.assemblyComponents,
    globalComponentsData,
  ]);

  const updateAssembly = useUpdateAssembly();
  const updateAssemblyComponents = useUpdateCreateDeleteAssemblyComponents();

  const { isViewOnly } = useUserRole();

  const onMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    menu.onOpenMenu(event.currentTarget);
  };

  const handleDeleteAssembly = async () => {
    isDeleted.current = true;
    await removeVariation.mutateAsync(Number(assemblyId));
    deleteAssemblyModal.onCloseModal();
    Snackbar.success(`${assemblyDetails?.name} ${lang.successfullyDeleted}`);
    navigate({
      pathname: PATHS.ASSEMBLIES,
    });
  };

  const handleConvertToDraft = async () => {
    setIsDisabled(true);
    try {
      const { data: originalAssemblyComponents, backupAssemblyComponents } =
        await fetchAssemblyComponents(toString(toNumber(assemblyId)));

      const assemblyComponents = originalAssemblyComponents.map((item) => ({
        ...item,
        status: IStatus.DRAFT,
      }));
      const backupComponents = backupAssemblyComponents.map((item) => ({
        ...item,
        status: IStatus.DRAFT,
      }));

      await Promise.all([
        updateAssemblyComponents.mutateAsync({
          data: [...assemblyComponents, ...backupComponents],
          assemblyId: toNumber(assemblyId),
          assemblyName: assemblyDetails?.name ?? '',
        }),
        updateAssembly.mutateAsync({
          id: toNumber(assemblyId),
          assemblyName: assemblyDetails?.name ?? '',
          status: IStatus.DRAFT,
        }),
      ]);

      convertToDraftModal.onCloseModal();

      Snackbar.success(
        `${assemblyDetails?.name} ${lang.successfullyConvertedToDraft}`,
      );
    } catch {
      Snackbar.error(`${lang.failedToConvert} ${assemblyDetails?.name}.`);
    } finally {
      setIsDisabled(false);
    }
  };

  const handleDuplicateComponentsConfirm = async () => {
    setIsDuplicated(true);
    try {
      await cloneResource.mutateAsync({
        type: 'assembly',
        id: toNumber(assemblyId),
        name: trim(duplicatedAssemblyName),
        narrative: (
          narrativeGenerators[COLLECTIONS.ASSEMBLIES][USER_ACTIONS.CREATE] as (
            duplicatedAssemblyName: string,
          ) => string
        )(trim(duplicatedAssemblyName)),
      });

      Snackbar.success(
        `${assemblyDetails?.name} ${lang.successfullyDuplicated}`,
      );
      setDuplicatedAssemblyName('');
      duplicateAssemblyModal.onCloseModal();
    } catch {
      Snackbar.error(lang.failedToDuplicate);
    } finally {
      setIsDuplicated(false);
      duplicateAssemblyModal.onCloseModal();
      setDuplicatedAssemblyName('');
    }
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { hasJourney } = checkSectionTypes(sectionTypes);

  return (
    <>
      {scheduleAssemblyId && locationCode ? null : (
        <AssemblyDetailContainer>
          <AssemblyDetailSideMenuContainer>
            {isLoadingAssemblyOverrides ? (
              <Progress />
            ) : (
              <>
                <AntTabs
                  value={value}
                  onChange={handleChange}
                  aria-label="ant example">
                  <AntTab label="Details" {...a11yProps(0)} />
                  <AntTab label="Components" {...a11yProps(1)} />
                  <AntTab label="History" {...a11yProps(3)} />
                </AntTabs>

                <DetailAssemblyTabPanel value={value} index={0}>
                  {assemblyDetails && (
                    <AssemblyDetailPageSidebar
                      assemblyDetails={assemblyDetails as IAssembly}
                      sectionTypes={sectionTypes}
                      setActiveAssemblyJourney={setActiveAssemblyJourney}
                      activeAssemblyJourney={activeAssemblyJourney}
                      setIsCodConfirmationOpen={setIsCodConfirmationOpen}
                      setCustomerState={setCustomerState}
                      customerState={customerState}
                      isExpiringSoon={isExpiringSoon}
                      onChangeDaypart={onChangeDaypart}
                      selectedDaypartId={selectedDaypartId}
                    />
                  )}
                </DetailAssemblyTabPanel>
                <DetailAssemblyTabPanel value={value} index={1}>
                  {layouts && (
                    <DetailAssemblyComponentList
                      layouts={layouts}
                      anchorEl={menu.anchorEl}
                      assemblyComponents={assemblyComponents || []}
                      parentAssemblyComponents={
                        assemblyComponentsDetails?.parentAssemblyComponents
                      }
                      assemblyId={toNumber(assemblyId)}
                    />
                  )}
                </DetailAssemblyTabPanel>
              </>
            )}
          </AssemblyDetailSideMenuContainer>
          <AssemblyDetailContentContainer ref={containerRef}>
            <HeaderContainer>
              {!isLoadingAssemblyOverrides && (
                <Box flex={1} overflow={'hidden'}>
                  {userRole !== AuthClassification.OWNER_OPERATOR && (
                    <Breadcrumbs
                      items={[
                        {
                          label: lang.assemblies,
                          link: PATHS.ASSEMBLIES,
                        },
                        {
                          label: lang.detail,
                        },
                      ]}
                    />
                  )}

                  <CustomTooltip
                    data-testid="assembly-name-custom-tooltip"
                    placement="bottom"
                    title={assemblyDetails?.name}
                    width={500}
                    overflowWidth={500}>
                    <AssemblyNameText variant="h3">
                      {assemblyDetails?.name}
                    </AssemblyNameText>
                  </CustomTooltip>
                </Box>
              )}
              {!isAssemblyDetailsLoading && (
                <ActionButtonsContainer
                  alignItems="center"
                  spacing={2}
                  direction="row">
                  {isViewOnly && <ViewOnlyAlert />}
                  {!location.pathname.includes('/overrides') &&
                    !location.pathname.includes('/scheduleComponent') && (
                      <CardMediaContainer>
                        <ComponentMenuIcon onClick={onMenuClick}>
                          <MoreHorizIcon />
                        </ComponentMenuIcon>
                      </CardMediaContainer>
                    )}

                  {assemblyDetails && (
                    <AssemblyMenu
                      anchorEl={menu.anchorEl}
                      onClose={menu.onCloseMenu}
                      row={assemblyDetails as IAssembly}
                      onDeleteClick={deleteAssemblyModal.onOpenModal}
                      onDuplicateClick={duplicateAssemblyModal.onOpenModal}
                      onConvertToDraftClick={convertToDraftModal.onOpenModal}
                      hasStageAssembly={isStageAssemblyIdMatched}
                      hasSavedAssembly={isSavedAssemblyIdMatched}
                    />
                  )}
                </ActionButtonsContainer>
              )}
              <SimpleDialog
                open={deleteAssemblyModal.open}
                cancelText={lang.cancel}
                confirmText={lang.deleteText}
                title={lang.deleteAssemblyModal.title}
                subTitle={lang.deleteAssemblyModal.subTitle}
                onClose={deleteAssemblyModal.onCloseModal}
                onClickCancel={deleteAssemblyModal.onCloseModal}
                modalSize="sm"
                disabled={isDeleted.current}
                onClickConfirm={handleDeleteAssembly}
              />
              <SimpleDialog
                open={convertToDraftModal.open}
                cancelText={lang.cancel}
                confirmText={lang.confirm}
                title={lang.convertToDraftModal.title}
                subTitle={lang.convertToDraftModal.subTitle}
                onClose={convertToDraftModal.onCloseModal}
                onClickCancel={convertToDraftModal.onCloseModal}
                modalSize="sm"
                disabled={isDisabled}
                onClickConfirm={handleConvertToDraft}
              />
              {duplicateAssemblyModal.open && (
                <SimpleDialog
                  title={lang.duplicateAssembly}
                  open={duplicateAssemblyModal.open}
                  confirmText={lang.create}
                  disabled={!trim(duplicatedAssemblyName) || isDuplicated}
                  onClose={duplicateAssemblyModal.onCloseModal}
                  onClickCancel={duplicateAssemblyModal.onCloseModal}
                  onClickConfirm={handleDuplicateComponentsConfirm}>
                  <>
                    <InputLabel>{lang.assemblyName}</InputLabel>
                    <TextField
                      fullWidth
                      onChange={(event) => {
                        setDuplicatedAssemblyName(event.target.value);
                      }}
                    />
                  </>
                </SimpleDialog>
              )}
            </HeaderContainer>

            {isAssemblyDetailsLoading ? (
              <Progress />
            ) : (
              <>
                {hasJourney && (
                  <CustomerStateAlert customerState={customerState} />
                )}
                <ScaleContainer enableScaleControls>
                  {layouts.map((layout) => (
                    <Grid item xs key={layout.id}>
                      <AssemblyLayout>
                        {layout.screens.map((screen, index) => (
                          <FullSizeAssemblyScreen
                            key={screen.name + index}
                            height={screen.height}
                            width={screen.width}
                            selectable={false}
                            type="screen">
                            {screen.sections.map((section, index) => {
                              const isGlobalSection =
                                isDisallowedOutdoorSection(layout, section);
                              const matchedAssmeblyComponents = isGlobalSection
                                ? (findMatchedGlobalComponents(
                                    section?.config?.suggestedComponentTypes,
                                    globalComponentsData,
                                  ) as IAssemblyComponent[])
                                : findMatchedAssemblyComponents(
                                    assemblyComponents,
                                    screen.layoutScreenId,
                                    section.screenSectionId,
                                    ['rotation_order'],
                                    assemblyComponentsDetails?.parentAssemblyComponents,
                                  );

                              return (
                                <FullSizeAssemblySection
                                  sectionTypes={sectionTypes}
                                  key={`${section.name}-${index}-${selectedDaypartId}-${customerState}`}
                                  section={section}
                                  activeAssemblyJourney={activeAssemblyJourney}
                                  showSectionBorder={false}
                                  isCodConfirmationOpen={isCodConfirmationOpen}
                                  isDisallowedOutdoorSection={isGlobalSection}
                                  productFilters={productFilters}
                                  productVariationFilters={
                                    productVariationFilters
                                  }
                                  skipProductsApiOnEmptyFilters={true}
                                  hasDaypartSelected={Boolean(
                                    globalComponentsData?.length,
                                  )}
                                  assemblyComponents={matchedAssmeblyComponents}
                                  customerState={customerState}
                                  selectedDaypartId={selectedDaypartId}
                                />
                              );
                            })}
                          </FullSizeAssemblyScreen>
                        ))}
                      </AssemblyLayout>
                    </Grid>
                  ))}
                </ScaleContainer>
              </>
            )}
          </AssemblyDetailContentContainer>
        </AssemblyDetailContainer>
      )}
    </>
  );
};
